import React, { useMemo, useState } from "react";
import { Mail, Plus, Check, HelpCircle, Trash2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { REQ_CATEGORIES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusChip, EmptyState } from "@/components/common";
import { ExtractDialog } from "@/components/opportunity/ExtractDialog";
import { cn } from "@/lib/utils";

const CATEGORY_HINT = {
  Purpose: "What the customer wants to achieve",
  Studio: "Room, cameras, audience, control room",
  Production: "Live, recording, streaming, podcast, remote",
  Technical: "Video format, IP / NDI / ST 2110, audio, lighting, network",
  Software: "MAM, automation, graphics, streaming platform, Teams",
  Delivery: "Deadline, installation, training, support",
  Commercial: "Budget, procurement, contract",
};

export function RequirementsTab({ data, reload, setTab }) {
  const o = data.opportunity;
  const reqs = data.requirements;
  const [extractOpen, setExtractOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: "Technical", title: "", detail: "", priority: "should", status: "identified" });

  const open = reqs.filter((r) => r.status === "needs_clarification");
  const grouped = useMemo(() => {
    const g = {};
    REQ_CATEGORIES.forEach((c) => { g[c] = []; });
    reqs.forEach((r) => { (g[r.category] = g[r.category] || []).push(r); });
    return g;
  }, [reqs]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/requirements/${id}`, body);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (id) => {
    await api.delete(`/requirements/${id}`);
    reload();
  };
  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await api.post(`/opportunities/${o.id}/requirements`, form);
      setForm({ ...form, title: "", detail: "" });
      setAdding(false);
      reload();
      toast.success("Requirement added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-[-0.02em]" data-testid="requirements-count-heading">{reqs.length} requirement{reqs.length === 1 ? "" : "s"} identified</h2>
          <p className="mt-1 text-sm text-muted-foreground">{open.length ? `${open.length} need clarification before the solution can be finalised.` : "Everything is clarified. Ready for solution design."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setExtractOpen(true)} data-testid="requirements-add-from-email-button"><Mail className="h-4 w-4" /> Add from e-mail / notes</Button>
          <Button variant="outline" className="gap-2" onClick={() => setAdding((v) => !v)} data-testid="requirements-add-manual-button"><Plus className="h-4 w-4" /> Add requirement</Button>
          {reqs.length > 0 && <Button className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" onClick={() => setTab("solution")} data-testid="requirements-go-to-solution-button"><Sparkles className="h-4 w-4" /> Generate solution <ArrowRight className="h-4 w-4" /></Button>}
        </div>
      </div>

      {adding && (
        <form onSubmit={add} className="mt-5 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-4 md:grid-cols-[160px_1fr_1fr_120px_auto]" data-testid="requirements-add-form">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="h-9 rounded-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent>{REQ_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Requirement (short)" className="h-9 rounded-[10px]" required data-testid="requirements-add-title-input" />
          <Input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="Detail (optional)" className="h-9 rounded-[10px]" />
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-9 rounded-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="must">Must</SelectItem><SelectItem value="should">Should</SelectItem><SelectItem value="nice">Nice</SelectItem></SelectContent>
          </Select>
          <Button type="submit" className="h-9 bg-primary text-white hover:bg-[#ff8e76]" data-testid="requirements-add-submit-button">Add</Button>
        </form>
      )}

      {reqs.length === 0 ? (
        <div className="mt-8">
          <EmptyState icon={HelpCircle} title="No requirements yet" description="Paste the customer's e-mail or meeting notes and the AI will structure the requirements for you." action={<Button className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" onClick={() => setExtractOpen(true)}><Mail className="h-4 w-4" /> Add from e-mail / notes</Button>} testId="requirements-empty" />
        </div>
      ) : (
        <>
          {open.length > 0 && (
            <section className="mt-8 rounded-2xl border border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.05)] p-5" data-testid="requirements-needs-clarification">
              <h3 className="flex items-center gap-2 text-sm font-semibold"><HelpCircle className="h-4 w-4 text-[hsl(var(--status-warn))]" /> Needs clarification ({open.length})</h3>
              <ul className="mt-3 divide-y divide-[hsl(var(--status-warn)/0.15)]">
                {open.map((r) => (
                  <li key={r.id} className="flex items-start gap-3 py-2.5 text-sm" data-testid="requirement-open-row">
                    <span className="mt-0.5 font-mono text-xs text-muted-foreground">?</span>
                    <div className="min-w-0 flex-1"><span className="font-medium">{r.title}</span>{r.detail ? <span className="text-muted-foreground"> – {r.detail}</span> : null}<span className="ml-2 rounded-full border bg-white px-1.5 text-[10px] text-muted-foreground">{r.category}</span></div>
                    <Button size="sm" variant="outline" className="h-7 gap-1 bg-white text-xs" onClick={() => patch(r.id, { status: "identified" })} data-testid="requirement-mark-clarified-button"><Check className="h-3 w-3" /> Clarified</Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-8 space-y-8">
            {REQ_CATEGORIES.concat(Object.keys(grouped).filter((k) => !REQ_CATEGORIES.includes(k))).map((cat) => {
              const items = (grouped[cat] || []).filter((r) => r.status !== "needs_clarification");
              if (items.length === 0) return null;
              return (
                <section key={cat} data-testid={`requirements-category-${cat.toLowerCase()}`}>
                  <div className="mb-2 flex items-baseline gap-3"><h3 className="text-base font-semibold">{cat}</h3><span className="text-xs text-muted-foreground">{CATEGORY_HINT[cat]}</span></div>
                  <ul className="divide-y rounded-2xl border bg-white">
                    {items.map((r) => (
                      <li key={r.id} className="group flex items-start gap-3 px-4 py-3 text-sm" data-testid="requirement-row">
                        <Check className={cn("mt-0.5 h-4 w-4 shrink-0", r.status === "resolved" ? "text-muted-foreground" : "text-[hsl(var(--status-ok))]")} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{r.title}</span><StatusChip status={r.priority} />{r.source && r.source !== "manual" && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">via {r.source}</span>}</div>
                          {r.detail ? <div className="mt-0.5 text-muted-foreground">{r.detail}</div> : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button type="button" title="Needs clarification" onClick={() => patch(r.id, { status: "needs_clarification" })} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="requirement-mark-open-button"><HelpCircle className="h-4 w-4" /></button>
                          <button type="button" title="Delete" onClick={() => remove(r.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" data-testid="requirement-delete-button"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}
      <ExtractDialog open={extractOpen} onOpenChange={setExtractOpen} opportunityId={o.id} onDone={reload} />
    </div>
  );
}
